import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	Check,
	CheckCircle2,
	Eye,
	EyeOff,
	KeyRound,
	Mail,
	X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import { HelmCompass } from "@/components/helm-logo";
import { API_BASE_URL } from "@/lib/consts";

// --- Route definition ---

export const Route = createFileRoute("/auth/reset-password")({
	component: ResetPasswordPage,
	validateSearch: (search) =>
		z.object({ token: z.string().min(1).optional() }).parse(search),
});

// --- Password validation (same rules as create-password) ---

interface PasswordRequirement {
	label: string;
	test: (pw: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
	{ label: "At least 8 characters", test: (pw) => pw.length >= 8 },
	{ label: "One uppercase letter", test: (pw) => /[A-Z]/.test(pw) },
	{ label: "One lowercase letter", test: (pw) => /[a-z]/.test(pw) },
	{ label: "One number", test: (pw) => /\d/.test(pw) },
	{
		label: "One special character",
		test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw),
	},
];

function getStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	if (!password) return { score: 0, label: "", color: "" };

	const passed = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;

	if (passed <= 2) return { score: 20, label: "Weak", color: "bg-red-500" };
	if (passed <= 3) return { score: 50, label: "Fair", color: "bg-amber-500" };
	if (passed <= 4) return { score: 75, label: "Good", color: "bg-blue-500" };
	return { score: 100, label: "Strong", color: "bg-green-500" };
}

// --- Main component ---

function ResetPasswordPage() {
	const { token } = Route.useSearch();

	// If token is present, show reset form; otherwise show request form
	if (token) {
		return <ResetPasswordForm token={token} />;
	}
	return <RequestResetForm />;
}

// --- Request Reset Form (no token) ---

function RequestResetForm() {
	const [email, setEmail] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!email || submitting) return;

			setSubmitting(true);
			setError(null);

			try {
				const res = await fetch(
					`${API_BASE_URL}/auth/request-password-reset`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ email }),
					},
				);

				if (res.ok) {
					setSubmitted(true);
				} else {
					const body = await res.json().catch(() => null);
					setError(
						body?.detail || "Something went wrong. Please try again.",
					);
				}
			} catch {
				setError(
					"Unable to connect. Please check your connection and try again.",
				);
			} finally {
				setSubmitting(false);
			}
		},
		[email, submitting],
	);

	return (
		<div className="flex min-h-svh items-center justify-center bg-muted/40 p-6">
			<div className="mx-auto w-full max-w-md space-y-8">
				<div className="flex justify-center">
					<HelmCompass
						size={32}
						color="hsl(22 82% 31%)"
						strokeWidth={1.2}
					/>
				</div>

				{submitted ? (
					<div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
							<Mail className="h-8 w-8 text-green-600" />
						</div>
						<h1 className="text-2xl font-semibold">Check Your Email</h1>
						<p className="text-muted-foreground">
							If an account exists with that email, we have sent a
							password reset link. Please check your inbox and spam
							folder.
						</p>
						<Button asChild variant="ghost" className="mt-2 w-full">
							<Link to="/login">Back to Login</Link>
						</Button>
					</div>
				) : (
					<div className="flex flex-col gap-6 rounded-lg border bg-card p-8 shadow-sm">
						<div className="space-y-2 text-center">
							<h1 className="text-2xl font-semibold">
								Reset Your Password
							</h1>
							<p className="text-sm text-muted-foreground">
								Enter your email address and we will send you a link to
								reset your password.
							</p>
						</div>

						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email">Email Address</Label>
								<Input
									id="email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="you@example.com"
									autoComplete="email"
									required
								/>
							</div>

							{error && (
								<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
									<p className="text-sm text-destructive">{error}</p>
								</div>
							)}

							<Button
								type="submit"
								className="w-full"
								disabled={!email || submitting}
							>
								{submitting ? (
									<>
										<Spinner className="mr-2 size-4" />
										Sending...
									</>
								) : (
									"Send Reset Link"
								)}
							</Button>
						</form>

						<div className="text-center">
							<Link
								to="/login"
								className="text-sm text-muted-foreground hover:text-foreground transition-colors"
							>
								Back to Login
							</Link>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

// --- Reset Password Form (with token) ---

type ResetState =
	| { status: "form" }
	| { status: "success" }
	| { status: "error"; message: string };

function ResetPasswordForm({ token }: { token: string }) {
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const [state, setState] = useState<ResetState>({ status: "form" });

	const requirementResults = useMemo(
		() =>
			PASSWORD_REQUIREMENTS.map((r) => ({ ...r, met: r.test(password) })),
		[password],
	);

	const allRequirementsMet = requirementResults.every((r) => r.met);
	const passwordsMatch =
		password === confirmPassword && confirmPassword.length > 0;
	const canSubmit = allRequirementsMet && passwordsMatch && !submitting;

	const strength = useMemo(() => getStrength(password), [password]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!canSubmit) return;

			setSubmitting(true);
			setFormError(null);

			try {
				const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						token,
						new_password: password,
						confirm_password: confirmPassword,
					}),
				});

				if (res.ok) {
					setState({ status: "success" });
					return;
				}

				const body = await res.json().catch(() => null);
				const detail = body?.detail;

				if (res.status === 410) {
					setState({
						status: "error",
						message:
							"This reset link has expired. Please request a new one.",
					});
					return;
				}

				if (typeof detail === "string") {
					setFormError(detail);
				} else if (detail && typeof detail === "object" && detail.message) {
					setFormError(detail.message);
				} else {
					setFormError("Something went wrong. Please try again.");
				}
			} catch {
				setFormError(
					"Unable to connect. Please check your connection and try again.",
				);
			} finally {
				setSubmitting(false);
			}
		},
		[canSubmit, token, password, confirmPassword],
	);

	return (
		<div className="flex min-h-svh items-center justify-center bg-muted/40 p-6">
			<div className="mx-auto w-full max-w-md space-y-8">
				<div className="flex justify-center">
					<HelmCompass
						size={32}
						color="hsl(22 82% 31%)"
						strokeWidth={1.2}
					/>
				</div>

				{state.status === "success" && (
					<div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
							<CheckCircle2 className="h-8 w-8 text-green-600" />
						</div>
						<h1 className="text-2xl font-semibold">Password Reset!</h1>
						<p className="text-muted-foreground">
							Your password has been reset successfully. You can now log
							in with your new password.
						</p>
						<Button asChild className="mt-2 w-full">
							<Link to="/login">Go to Login</Link>
						</Button>
					</div>
				)}

				{state.status === "error" && (
					<div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
							<AlertCircle className="h-8 w-8 text-red-600" />
						</div>
						<h1 className="text-2xl font-semibold">Reset Failed</h1>
						<p className="text-muted-foreground">{state.message}</p>
						<div className="mt-2 flex w-full flex-col gap-2">
							<Button asChild className="w-full">
								<Link to="/auth/reset-password">
									Request New Reset Link
								</Link>
							</Button>
							<Button asChild variant="ghost" className="w-full">
								<Link to="/login">Back to Login</Link>
							</Button>
						</div>
					</div>
				)}

				{state.status === "form" && (
					<div className="flex flex-col gap-6 rounded-lg border bg-card p-8 shadow-sm">
						<div className="space-y-2 text-center">
							<div className="flex items-center justify-center gap-2 text-primary">
								<KeyRound className="size-5" />
							</div>
							<h1 className="text-2xl font-semibold">
								Create New Password
							</h1>
							<p className="text-sm text-muted-foreground">
								Choose a strong password with at least 8 characters.
							</p>
						</div>

						<form onSubmit={handleSubmit} className="space-y-5">
							{/* Password field */}
							<div className="space-y-2">
								<Label htmlFor="password">New Password</Label>
								<div className="relative">
									<Input
										id="password"
										type={showPassword ? "text" : "password"}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										placeholder="Enter your new password"
										className="pr-10"
										autoComplete="new-password"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
										aria-label={
											showPassword
												? "Hide password"
												: "Show password"
										}
									>
										{showPassword ? (
											<EyeOff className="size-4" />
										) : (
											<Eye className="size-4" />
										)}
									</button>
								</div>
							</div>

							{/* Strength indicator */}
							{password.length > 0 && (
								<div className="space-y-2">
									<div className="flex items-center justify-between text-xs">
										<span className="text-muted-foreground">
											Password strength
										</span>
										<span
											className={`font-medium ${
												strength.score <= 20
													? "text-red-500"
													: strength.score <= 50
														? "text-amber-500"
														: strength.score <= 75
															? "text-blue-500"
															: "text-green-500"
											}`}
										>
											{strength.label}
										</span>
									</div>
									<Progress
										value={strength.score}
										className={`h-1.5 ${strength.color.replace("bg-", "[&>[data-slot=progress-indicator]]:bg-")}`}
									/>
								</div>
							)}

							{/* Requirements checklist */}
							{password.length > 0 && (
								<div className="space-y-1.5">
									{requirementResults.map((req) => (
										<div
											key={req.label}
											className="flex items-center gap-2 text-sm"
										>
											{req.met ? (
												<Check className="size-4 text-green-500 shrink-0" />
											) : (
												<X className="size-4 text-muted-foreground/50 shrink-0" />
											)}
											<span
												className={
													req.met
														? "text-green-600 dark:text-green-400"
														: "text-muted-foreground"
												}
											>
												{req.label}
											</span>
										</div>
									))}
								</div>
							)}

							{/* Confirm password field */}
							<div className="space-y-2">
								<Label htmlFor="confirm-password">
									Confirm New Password
								</Label>
								<div className="relative">
									<Input
										id="confirm-password"
										type={showConfirm ? "text" : "password"}
										value={confirmPassword}
										onChange={(e) =>
											setConfirmPassword(e.target.value)
										}
										placeholder="Confirm your new password"
										className="pr-10"
										autoComplete="new-password"
									/>
									<button
										type="button"
										onClick={() => setShowConfirm(!showConfirm)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
										aria-label={
											showConfirm
												? "Hide confirm password"
												: "Show confirm password"
										}
									>
										{showConfirm ? (
											<EyeOff className="size-4" />
										) : (
											<Eye className="size-4" />
										)}
									</button>
								</div>
								{confirmPassword.length > 0 && !passwordsMatch && (
									<p className="text-sm text-destructive">
										Passwords do not match
									</p>
								)}
								{passwordsMatch && (
									<p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
										<Check className="size-3.5" />
										Passwords match
									</p>
								)}
							</div>

							{/* Error message */}
							{formError && (
								<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
									<p className="text-sm text-destructive">
										{formError}
									</p>
								</div>
							)}

							{/* Submit button */}
							<Button
								type="submit"
								className="w-full"
								disabled={!canSubmit}
							>
								{submitting ? (
									<>
										<Spinner className="mr-2 size-4" />
										Resetting Password...
									</>
								) : (
									"Reset Password"
								)}
							</Button>
						</form>

						<div className="text-center">
							<Link
								to="/login"
								className="text-sm text-muted-foreground hover:text-foreground transition-colors"
							>
								Back to Login
							</Link>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
